// Single source for writing the order-list filter state to the browser URL.
//
// The filter state itself is computed by the (now pure) QueryReducer, which
// exposes the serialized filter query as `state.query.urlQuerySerialized`.
// Mutating `window.history` is a side effect and has no business living inside a
// reducer, so it lives here: after every action, if the serialized filter query
// changed, this middleware reflects it into the URL.
//
// Only the keys the filter system owns (FILTER_KEYS) are touched. Any foreign
// query params already in the URL — payment-return markers (?payment=…), UTM
// tags, etc. — are read live and preserved, so they are never dropped and never
// resurrected (the bug that came from the reducer rebuilding the whole query
// string from a boot-time snapshot).
//
// Curried `() => store => next => action` to match the other core middleware
// (it is called with `()` in configureStore). `applyMiddleware` passes the
// store API so we can read the post-reducer state.

import {FILTER_KEYS} from '../reducers/QueryReducer';

const queryUrlSyncMiddleware = () => (store) => (next) => (action) => {
    if (typeof window === 'undefined') {
        return next(action);
    }

    const before = store.getState().query?.urlQuerySerialized;
    const result = next(action);
    const after = store.getState().query?.urlQuerySerialized;

    if (after !== before) {
        const params = new URLSearchParams(window.location.search);
        FILTER_KEYS.forEach((key) => params.delete(key));

        const foreign = params.toString();
        const merged = [foreign, after].filter(Boolean).join('&');
        const url = window.location.pathname + (merged ? `?${merged}` : '');

        window.history.pushState({}, '', url);
    }

    return result;
};

export default queryUrlSyncMiddleware;
